import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, CalendarDays, Phone, Facebook } from "lucide-react";
// CategoryNav removed
// InstagramWidget removed — only on home page
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";
import FilterChip from "./FilterChip";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";

const EVENT_FIELDS: FieldDef[] = [
  { key: "name", label: "Event name", required: true },
  { key: "date", label: "Date (YYYY-MM-DD)", placeholder: "2026-09-27" },
  { key: "endDate", label: "End date (YYYY-MM-DD, optional)", placeholder: "2026-09-28" },
  { key: "time", label: "Time", placeholder: "7:00 PM" },
  { key: "venue", label: "Venue", type: "textarea" },
  { key: "city", label: "City", placeholder: "Frisco" },
  { key: "category", label: "Category", type: "select", options: ["Concert", "Festival", "Expo", "Theater & Plays"] },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "link", label: "Tickets link", type: "url" },
  { key: "websiteLink", label: "Website link", type: "url" },
];

export interface EventItem {
  id: string;
  name: string;
  date?: string;      // ISO yyyy-mm-dd (start) — omit for undated announcements
  endDate?: string;  // ISO yyyy-mm-dd (multi-day)
  time?: string;
  venue?: string;
  city?: string;
  category?: "Concert" | "Festival" | "Expo" | "Theater & Plays";
  image: string;
  link?: string;
  websiteLink?: string;
  isFree?: boolean;
  featuredBy?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  contact?: string;
  contactUrl?: string;
  contacts?: string[]; // Multiple phone numbers — each renders its own green Call button (number hidden on button)
  address?: string; // Optional full address for View Location button
  price?: string; // Entry fee label, e.g. \"$20.00\"
  description?: string; // Optional free-text blurb shown instead of the date/venue line
  detailOnlyDescription?: boolean; // When true, description renders ONLY on the detail page, never on cards
  sessions?: string[]; // Multiple non-contiguous date/time lines, e.g. multi-session workshops
}

// Curated DFW Indian-community events (sourced from the web). The page splits these
// into Upcoming vs Past automatically by comparing each event's date to TODAY, so an
// event rolls into \"Past\" the day after it happens — works every month, no edits.
export const EVENTS: EventItem[] = [
  // ── Upcoming (Jul–Sep 2026) ──
  {
    id: "mehfil-market-north-dallas",
    name: "Mehfil Market",
    date: "2026-11-14",
    time: "12:00 PM to 6:00 PM",
    category: "Expo",
    city: "Pilot Point",
    address: "9943 FM1385, Pilot Point, TX 76258",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-18/24f3ce04d9d0.jpeg",
    instagramUrl: "https://www.instagram.com/jodo_events?rpxt=MXhiNzE3YTM5ZXV6dA%3D%3D&utm_source=qr",
    link: "https://docs.google.com/forms/d/e/1FAIpQLSfyQuO5MAMoO-XMQ1sxZ9-o8_qnhX6BD02jDhhT-PkXiOi72g/viewform?usp=sharing&ouid=115003647846982788603",
    detailOnlyDescription: true,
    description: "Mehfil Market is a community market designed to support small south asian businesses and local artists. We are looking to bring together an afternoon of community, shopping, and entertainment to the north Dallas area",
  },
  {
    id: "falguni-pathak-dandiya-dhoom-2026",
    name: "Falguni Pathak — Dandiya Dhoom 2026",
    date: "2026-10-02",
    time: "7:00 PM CDT",
    venue: "Frisco Flyers, Frisco, Texas",
    city: "Frisco",
    category: "Festival",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1789494926240-k9j5z.jpg",
    link: "https://www.endlessevent.com/redirect?eventURL=falguni-pathak-live-in-dallas-dandiya-dhoom-2026-event-in-frisco-texas-usa",
  },
  {
    id: "rahat-fateh-ali-khan-dallas-2026",
    name: "Rahat Fateh Ali Khan Live In Dallas",
    date: "2026-10-16",
    time: "8:30 PM",
    venue: "Comerica Center, 2601 Avenue of the Stars",
    city: "Frisco",
    category: "Concert",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1789494619243-hqrc9.jpg",
    address: "Comerica Center, 2601 Avenue of the Stars, Frisco, TX 75034",
    link: "https://www.ticketmaster.com/rahat-fateh-ali-khan-live-in-frisco-texas-10-16-2026/event/0C0064988E5383E8?_gl=1*6oon5n*_ga*NjI4NzM4Mjk2LjE3ODk0MDU2ODM.*_ga_C1T806G4DF*czE3ODk0MDU2ODIkbzEkZzAkdDE3ODk0MDU2ODIkajYwJGwwJGgw",
  },
  {
    id: "aishwarya-majmudar-rangtaali-2026",
    name: "Aishwarya Majmudar — Rangtaali 2026, The Finale Dallas",
    date: "2026-10-23",
    time: "Doors 7:00 PM | Garba 8:00 PM",
    venue: "Frisco Flyers Event Center, 6000 Flyers Way",
    city: "Frisco",
    category: "Festival",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1789494760104-c7rpxh.jpg",
    address: "Frisco Flyers Event Center, 6000 Flyers Way, Frisco, TX 75034",
    price: "$30 (Patel Brothers Special: $25)",
    contacts: ["(832) 640-9597", "(281) 310-7469"],
  },
  {
    id: "shree-pandokhar-sarkar-divine-evening-dallas-2026",
    name: "Divine Evening with Shree Pandokhar Sarkar",
    date: "2026-10-30",
    endDate: "2026-10-31",
    time: "6:00 PM - 9:00 PM",
    venue: "Shree Raghunath Temple",
    city: "Plano",
    category: "Festival",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-15/a95af96365a0.jpeg",
    address: "Shree Raghunath Temple, 8901 Independence Pkwy Ste 100, Plano, TX 75025",
    contact: "3145706525",
    detailOnlyDescription: true,
    description: "Join us for a divine evening with Shree Pandokhar Sarkar in Dallas, TX, on Friday, October 30, 2026, from 6:00 PM to 9:00 PM at Shree Raghunath Temple, Plano. Experience soulful bhajans, Divya Darbar, and Mahaprasad in a spiritually uplifting atmosphere. On Saturday, October 31, 1:1 consultations will also be available. RSVP is required, and seating is limited",
  },
  {
    id: "unity-tour-ammy-virk-live-2026",
    name: "UNITY TOUR 'Ammy Virk Live!'",
    date: "2026-08-29",
    time: "9:00 PM",
    venue: "The Rayleigh Underground, 316 West Las Colinas Boulevard suite 100",
    city: "Irving",
    category: "Concert",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787013047657-ypg9ki.jpg",
    address: "316 West Las Colinas Boulevard suite 100, Irving, TX 75039",
    contacts: ["855-360-7469"],
  },
  {
    id: "dallas-exclusive-dance-workshop-2026",
    name: "Dallas Exclusive Dance Workshop",
    date: "2026-08-15",
    time: "5:30 PM - 7:30 PM",
    venue: "Richwoods Academy, 4633 Coit Rd #310",
    city: "Frisco",
    category: "Festival",
    image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-11/73695939d1f9.png",
    address: "Richwoods Academy, 4633 Coit Rd #310, Frisco, TX 75035",
  },
  {
    id: "jhankaar-beats-live-musical-performance-2026",
    name: "Jhankaar Beats - Live Musical Performance",
    date: "2026-08-13",
    time: "7:30 PM onwards",
    venue: "GOLI SODA, 6201 S Custer Rd #100",
    city: "McKinney",
    category: "Concert",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-08-10/52bba561e87f.jpeg",
    address: "GOLI SODA, 6201 S Custer Rd #100, McKinney, TX 75070",
  },
  {
    id: "house-of-chic-festive-edit-frisco-2026",
    name: "House of Chic — Festive Edit Pop-Up Shop (Heena • Suri)",
    date: "2026-09-13",
    time: "12:00 PM - 8:00 PM",
    venue: "Frisco Hall, 5353 Independence Pkwy, Suite 1",
    city: "Frisco",
    category: "Expo",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785890372169-lxj1ws.png",
    address: "5353 Independence Pkwy, Suite 1, Frisco, TX 75035",
    contacts: ["+1 (914) 531-0011"],
  },
  {
    id: "indiglam-ethnic-studio-neelima-2026",
    name: "IndiGlam and Ethnic Studio by Neelima",
    date: "2026-08-08",
    time: "12:00 PM - 8:00 PM",
    venue: "Sheraton Stonebriar Hotel",
    city: "Frisco",
    category: "Expo",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-08-01/668d6e91edf1.jpeg",
    address: "Sheraton hotel, 5444 State Hwy 121, Frisco, TX 75034",
    instagramUrl: "https://www.instagram.com/ethnicstudiobyneelima?igsh=ZGJkaXQwcTI4aW5t&utm_source=qr",
  },
  {
    id: "dj-shadow-live-dallas",
    name: "DJ SHADOW Live In Dallas",
    date: "2026-08-14",
    time: "10:00 PM",
    venue: "Sambuca 360",
    city: "Plano",
    category: "Concert",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783896899299-tqslyc.jpg",
    address: "7200 Bishop Rd #270, plano, tx 75024"
  },
  { id: "wedding-expo-2026", name: "Wedding Expo", date: "2026-09-20", time: "10:00 AM", venue: "FRISCO HALL CENTER", city: "Frisco", category: "Expo", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781560814542-m61py6.jpg", websiteLink: "https://flashbrushbridalexpo.com/", isFree: true, featuredBy: "TastebudsByAnubhi", instagramUrl: "https://www.instagram.com/reels/DQP7NdxEq01/" },
  { id: "ammy-virk-2026", name: "Ammy Virk Live!", date: "2026-08-15", time: "8:30 PM", venue: "Credit Union of Texas Event Center", city: "Allen", category: "Concert", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781560858668-grdvd.jpg", link: "https://www.ticketmaster.com/event/0C0064B3C6A4AB82?utm_source=ig&utm_medium=social&utm_content=link_in_bio" },
  { id: "sonu-nigam-revolution-2026", name: "Sonu Nigam — Revolution Tour", date: "2026-09-26", time: "8:30 PM", venue: "Credit Union of Texas Event Center, 200 E Stacy Rd Unit 1350", city: "Allen", category: "Concert", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783696223175-nrb38w.jpg", link: "https://www.ticketmaster.com/sonu-nigam-allen-texas-09-26-2026/event/0C00649878275CE9" },
  { id: "prabhu-deva-live-usa-2026", name: "Prabhu Deva Live USA", date: "2026-08-01", time: "7:00 PM", venue: "Texas Trust CU Theatre, 1001 Texas Trust Way", city: "Grand Prairie", category: "Concert", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783438898516-t4hk8b.jpg", link: "https://www.axs.com/events/1485523/prabhudeva-live-usa-tickets" },
  { id: "hindu-rishte-match-mingle-2026", name: "Hindu Rishte — Match & Mingle", date: "2026-08-08", time: "11:00 AM (Registration starts 10:30 AM)", venue: "SpringHill Suites by Marriott, 8401 Angels Dr", city: "Plano", category: "Festival", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783522363301-kz0yln.jpg", websiteLink: "https://www.gujarati.life/Match2026", contact: "(214) 733-0716", featuredBy: "DFW Gujarati Samaj" },
  { id: "great-indian-diwali-grandscape-2026", name: "The Great Indian Diwali - Festival of Lights at Grandscape", date: "2026-10-25", time: "12:01 PM", venue: "Grandscape", city: "The Colony", category: "Festival", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-18/9e17683355af.jpeg", address: "5752 Grandscape Blvd, The Colony, TX 75056", contact: "919-641-9799" },
  { id: "govindjis-fashion-show-2026", name: "Govindji's Fashion Show", date: "2026-08-01", time: "3:00 PM - 6:00 PM", venue: "Govindji's", city: "Frisco", category: "Expo", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-19/51cb04e5aba3.jpeg", address: "4646 Dubai Way, Frisco, TX 75034", contact: "4697315122", isFree: true },
  { id: "comedy-lane-mushaira-rising-performance-hall-2026", name: "Comedy Lane Mushaira at Rising Performance Hall", date: "2026-08-02", time: "5:30 PM - 9:00 PM", venue: "Rising Performance Hall", city: "Carrollton", category: "Theater & Plays", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-19/b219023e181d.jpeg", address: "1446 Halsey Way # 114, Carrollton, TX 75007", price: "$25.00 (Refreshment and Drink Included) · Zelle: 817-323-4018", contacts: ["517-918-7789", "817-323-4018"] },
  { id: "kanjivaram-sarees-festival-show-2026", name: "Festival Special Show, New Exquisite collection of Pure Kanjivaram Handloom sarees", date: "2026-08-01", time: "3:00 PM - 7:00 PM", venue: "iBloom Montessori", city: "Irving", category: "Expo", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/a2070e0ff2c7.jpeg", address: "iBloom Montessori, 7650 Bent Branch Dr, Irving, TX 75063", link: "https://facebook.com/events/s/sarees-and-jewelry-exhibition-/1739452967381079/" },
  { id: "49th-anand-bazaar-2026", name: "49th Anand Bazaar", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-23/a25d16375bb2.jpeg", date: "2026-08-15", time: "2:00 PM onwards", address: "Comerica Center, 2601 Avenue of the Stars, Frisco, TX 75034, United States", facebookUrl: "https://www.facebook.com/IndiaAssociationOfNorthTexas" },
];

const EVENT_CATEGORIES: EventItem["category"][] = ["Concert", "Festival", "Expo", "Theater & Plays"];

const CATEGORY_COLOR: Record<NonNullable<EventItem["category"]>, string> = {
  Concert: "#8B5CF6",
  Festival: "#f97316",

  Expo: "#2563eb",
  "Theater & Plays": "#ec4899",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Canonical slug for an event's detail page (/events/:slug) — same key the
// admin-managed collection uses, so detail pages survive admin edits.
export function eventSlug(e: Pick<EventItem, "id" | "name">): string {
  return slugify(e.id || e.name);
}

export function parseLocal(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
export function startOfToday(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}
// Returns true if the event's END date/time (endDate if present, else date) is
// today or in the future — i.e. it hasn't finished yet.
export function isEventUpcoming(e: EventItem): boolean {
  if (!e.date) return true; // undated announcements always show as upcoming
  const end = e.endDate ? parseLocal(e.endDate) : parseLocal(e.date);
  return end >= startOfToday();
}
// Returns true if the event falls (starts, ends, or spans) within the given month/year.
export function isEventInMonth(e: EventItem, year: number, month: number): boolean {
  if (!e.date) return false;
  const start = parseLocal(e.date);
  const end = e.endDate ? parseLocal(e.endDate) : start;
  const target = new Date(year, month, 1);
  const targetEnd = new Date(year, month + 1, 0);
  return start <= targetEnd && end >= target;
}
function formatRange(e: EventItem): string {
  if (!e.date) return "";
  const s = parseLocal(e.date);
  if (e.endDate) {
    const en = parseLocal(e.endDate);
    return `${MONTHS[s.getMonth()]} ${s.getDate()}–${en.getDate()}, ${s.getFullYear()}`;
  }
  return `${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`;
}
export function EventCard({ event, index, past }: { event: EventItem; index: number; past?: boolean }) {
  const s = event.date ? parseLocal(event.date) : null;
  const color = event.category ? CATEGORY_COLOR[event.category] : "#8B5CF6";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className={`bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col ${past ? "opacity-90" : ""}`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/events/${eventSlug(event)}`} className="block w-full aspect-[3/4] relative overflow-hidden" style={{ background: past ? "#9ca3af" : color }}>
        <img
          src={event.image}
          alt={event.name}
          className="w-full h-full object-cover"
          loading="lazy"
          style={past ? { filter: "grayscale(0.5)" } : undefined}
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
        {s && (
          <div className="absolute top-1.5 left-1.5 bg-white/95 rounded px-1.5 py-1 text-center shadow-sm leading-none">
            <div className="text-[9px] font-bold uppercase" style={{ color: past ? "#6b7280" : color }}>{MONTHS[s.getMonth()]}</div>
            <div className="text-[13px] font-extrabold text-gray-900">{s.getDate()}</div>
          </div>
        )}
        {event.category && (
          <span className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white" style={{ background: past ? "#6b7280" : color }}>
            {past ? "Past" : event.category}
          </span>
        )}
        {event.isFree && (
          <span className="absolute bottom-2 left-2 right-2 text-center text-[11px] font-extrabold uppercase tracking-wider px-2 py-1 rounded bg-green-600 text-white">
            FREE EVENT
          </span>
        )}
      </Link>

      <div className="p-2.5 pb-1.5 flex-1">
        <h3 className="text-[13px] font-bold text-gray-900 leading-snug mb-1 line-clamp-2">
          <Link to={`/events/${eventSlug(event)}`} className="hover:text-orange-600 transition-colors">
            {event.name}
          </Link>
        </h3>
        {event.date && (
          <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-1">
            <CalendarDays className="w-3 h-3 text-gray-400 shrink-0" />
            <span className="truncate">{formatRange(event)}{event.time ? ` · ${event.time}` : ""}</span>
          </div>
        )}
        {event.venue && (
          <div className="flex items-start gap-1 text-[11px] text-gray-500">
            <MapPin className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
            <span className="leading-snug">{event.venue}{event.city ? `, ${event.city}, TX` : ""}</span>
          </div>
        )}
        {event.address && !event.venue && (
          <div className="flex items-start gap-1 text-[11px] text-gray-500 mb-1">
            <MapPin className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
            <span className="leading-snug">{event.address}</span>
          </div>
        )}
        {event.sessions && event.sessions.map((s, si) => (
          <div key={si} className="flex items-center gap-1 text-[11px] text-gray-500 mb-1">
            <CalendarDays className="w-3 h-3 text-gray-400 shrink-0" />
            <span className="truncate">{s}</span>
          </div>
        ))}
        {event.description && !event.detailOnlyDescription && (
          <p className="text-[11px] text-gray-500 leading-snug mt-1 line-clamp-3">{event.description}</p>
        )}
        {event.price && (
          <div className="mt-1.5 inline-block text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
            Entry Fee: {event.price}
          </div>
        )}
        {event.featuredBy && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-[10px] font-semibold text-pink-600 bg-pink-50 px-1.5 py-0.5 rounded-full">
              Featured by {event.featuredBy}
            </span>
          </div>
        )}
      </div>

      <div className="px-2.5 pb-2.5 pt-0.5 flex flex-wrap gap-1.5">
        {event.address && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <MapPin className="w-3 h-3" /> View Location
          </a>
        )}
        {event.link && (
          <a href={event.link} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <ExternalLink className="w-3 h-3" /> Get Tickets
          </a>
        )}
        {event.websiteLink && (
          <a href={"https://" + String(event.websiteLink).replace(/^https?:\/\//i, "")} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <ExternalLink className="w-3 h-3" /> Website
          </a>
        )}
        {event.instagramUrl && (
          <a href={"https://" + String(event.instagramUrl).replace(/^https?:\/\//i, "")} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 text-white hover:opacity-90 text-[11px] font-semibold py-1.5 rounded-md transition-opacity">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> Instagram
          </a>
        )}
        {event.facebookUrl && (
          <a href={"https://" + String(event.facebookUrl).replace(/^https?:\/\//i, "")} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-[#1877F2] border border-[#1877F2] text-white hover:bg-[#166fe0] text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <Facebook className="w-3 h-3" /> Facebook
          </a>
        )}
        {event.contact && !event.contactUrl && (
          <a href={`tel:${event.contact.replace(/[^\d+]/g, "")}`} className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] border border-[#51ba4f] text-white hover:bg-[#459f43] text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <Phone className="w-3 h-3" /> Call
          </a>
        )}
        {event.contactUrl && (
          <a href={event.contactUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] border border-[#51ba4f] text-white hover:bg-[#459f43] text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <Phone className="w-3 h-3" /> Call
          </a>
        )}
        {event.contacts && event.contacts.map((num, ci) => (
          <a key={ci} href={`tel:${num.replace(/[^\d+]/g, "")}`} className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] border border-[#51ba4f] text-white hover:bg-[#459f43] text-[11px] font-semibold py-1.5 rounded-md transition-colors">
            <Phone className="w-3 h-3" /> Call
          </a>
        ))}
      </div>
    </motion.div>
  );
}

export default function Events({ title = "Events" }: { title?: string }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<EventItem["category"] | null>(null);
  const [freeOnly, setFreeOnly] = useState(false);

  const mc = useManagedCollection<any>("events_featured", EVENTS, (e) => slugify(e.id || e.name));

  const today = startOfToday();
  // Undated announcements (no `date`, e.g. a workshop with no fixed date) have no end
  // to compute — treat as always-upcoming instead of crashing on parseLocal(undefined).
  const endOf = (e: EventItem) => (e.date ? parseLocal(e.endDate || e.date) : null);
  const matches = (e: EventItem) =>
    (query.trim() === "" || e.name.toLowerCase().includes(query.trim().toLowerCase())) &&
    (!category || e.category === category) &&
    (!freeOnly || e.isFree);

  const filtered = mc.items.filter(matches);
  const upcoming = filtered
    .filter((e) => { const end = endOf(e); return !end || end >= today; })
    .sort((a, b) => {
      if (!a.date) return -1;
      if (!b.date) return 1;
      return parseLocal(a.date).getTime() - parseLocal(b.date).getTime();
    });
  const past = filtered
    .filter((e) => { const end = endOf(e); return end !== null && end < today; })
    .sort((a, b) => parseLocal(b.date!).getTime() - parseLocal(a.date!).getTime());

  const gridClass = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3";

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title={title}
        subtitle="Indian community events across Dallas–Fort Worth"
        placeholder="Search events…"
        query={query}
        setQuery={setQuery}
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-25/cee04f47a780.png"
      />
      <NewsTicker />
      {/* Filter bar */}
      <div className="bg-white border-b border-gray-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-6 pb-4">
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ letterSpacing: "0.12em" }}>Explore by Type</p>
            <div className="flex gap-2 flex-wrap items-center">
              {EVENT_CATEGORIES.map((c) => (
                <FilterChip key={c} active={category === c} color="#16a34a" onClick={() => setCategory(category === c ? null : c)}>{c}</FilterChip>
              ))}
              <FilterChip active={freeOnly} color="#16a34a" onClick={() => setFreeOnly(!freeOnly)}>Free Event</FilterChip>
              {(category || freeOnly) && (
                <button
                  onClick={() => { setCategory(null); setFreeOnly(false); }}
                  className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 rounded-full px-3 py-1.5 transition-colors"
                >
                  ✕ Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pb-6">
        <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>
          Dallas Desi Concerts &amp; Events in DFW
        </h1>
        <p className="text-sm text-gray-600 leading-relaxed max-w-[900px]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Discover Dallas Desi concerts, Indian concerts, South Asian events, cultural programs, festivals, and live entertainment across the Dallas–Fort Worth area with DallasDesiScoop. Find upcoming Telugu concerts, Bollywood concerts, Indian music events, Pakistani events, live shows, comedy shows, dance performances, cultural festivals, community events, and family-friendly Desi events in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, Lewisville, Allen, and surrounding DFW communities.
        </p>
        <p className="mt-2">
          <Link
            to="/blog/upcoming-desi-events-dfw"
            className="text-sm font-semibold underline underline-offset-2 text-orange-600"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Read our DFW Desi events guide →
          </Link>
        </p>
        <p className="mt-1">
          <Link
            to="/blog"
            className="text-sm font-semibold underline underline-offset-2 text-orange-600"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Explore all DFW Desi guides on our blog →
          </Link>
        </p>
      </div>

      <section className="bg-gray-50/40 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Upcoming Events/Concerts
            </h2>

            <AdminAddBar mc={mc} fields={EVENT_FIELDS} label="Add Event" />

            {upcoming.length === 0 ? (
              <p className="text-gray-500 text-sm">No upcoming events match your filters.</p>
            ) : (
              <div className={gridClass}>
                {upcoming.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={EVENT_FIELDS} title="Event">
                    <EventCard event={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            )}

            {past.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Past Events
                </h2>
                <div className={gridClass}>
                  {past.map((e, i) => (
                    <AdminItem key={e._key} mc={mc} item={e} fields={EVENT_FIELDS} title="Event">
                      <EventCard event={e as any} index={i} past />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Instagram widget removed — only on home page */}
        </div>
      </section>

      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-8">
        <p className="text-sm text-gray-600 leading-relaxed max-w-[900px]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Looking for Dallas Desi events, Dallas Desi concerts, Indian events in Dallas, Telugu events in DFW, Bollywood events, South Asian concerts, Pakistani events, Indian cultural events, Desi festivals, live music, celebrity concerts, or community gatherings near you? DallasDesiScoop helps you discover upcoming concerts and events across DFW, including Indian and Pakistani music concerts, Telugu and Hindi events, Bollywood shows, cultural celebrations, dance performances, comedy shows, festivals, family events, and other Desi entertainment. Check back regularly to find what's happening in the Dallas–Fort Worth Desi community and discover upcoming events near you.
        </p>
      </div>

    </div>
  );
}
